import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import PackageOfferCard from "../../components/PackageOfferCard";
import PackageBuyPromptModal from "../../components/PackageBuyPromptModal";
import ShopHero from "../../components/ShopHero";
import "./ShopPackages.css";

/**
 * Every package this shop sells, at /business/:businessSlug/:locationSlug/packages.
 *
 * Created 2026-11-03: the shop page could only ever show its packages in a sideways-scrolling strip,
 * so a customer who wanted to compare them had to swipe and remember. This is the same page-level
 * treatment the shop already has for services, looks and professionals.
 *
 * Selection opens the same Buy package prompt the shop page uses — one purchase flow, one set of words.
 */
const ShopPackages = () => {
  const { businessSlug, locationSlug } = useParams();
  const navigate = useNavigate();

  const [shop, setShop] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [buyPromptPackage, setBuyPromptPackage] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // The hero offers "Shop Catalogue" only when the shop actually sells products.
  const [hasProducts, setHasProducts] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    fetchPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessSlug, locationSlug]);

  const fetchPackages = async () => {
    try {
      setLoading(true);

      const shopRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/business/${businessSlug}/${locationSlug}`,
      );
      if (!shopRes.ok) throw new Error("Shop location not found");
      const shopData = await shopRes.json();
      setShop(shopData);

      // Does this shop sell products? Drives the hero's "Shop Catalogue" button, as on the shop page.
      fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/products/shop/${shopData._id}?featured=true&limit=1`,
      )
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const list = data?.products || data || [];
          if (Array.isArray(list)) setHasProducts(list.length > 0);
        })
        .catch(() => {});

      const packagesRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/packages/business/${shopData.businessId}/public`,
      );
      if (packagesRes.ok) {
        const data = await packagesRes.json();
        const sorted = (data.packages || []).slice().sort((a, b) => {
          // Savings first, then cheapest — the same ordering the shop page uses.
          const saving = (p) =>
            p.regularPrice && p.packagePrice ? p.regularPrice - p.packagePrice : 0;
          const bySaving = saving(b) - saving(a);
          return bySaving !== 0
            ? bySaving
            : Number(a.packagePrice || 0) - Number(b.packagePrice || 0);
        });
        setPackages(sorted);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const locationLabel = [shop?.city, shop?.state].filter(Boolean).join(", ");

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div className="pkg-page">
          <div className="pkg-status">
            <p className="pkg-status-text">Loading packages…</p>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (error || !shop) {
    return (
      <>
        <CustomerHeader />
        <div className="pkg-page">
          <div className="pkg-status">
            <p className="pkg-status-text pkg-status-error">
              {error || "Shop location not found"}
            </p>
            <button
              type="button"
              className="pkg-status-button"
              onClick={() =>
                navigate(`/business/${businessSlug}/${locationSlug}`)
              }
            >
              Back to shop
            </button>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  return (
    <>
      <CustomerHeader />
      <div className="pkg-page">
        {/* Same hero as the shop and services pages — this is a continuation of the storefront. */}
        <ShopHero
          name={shop.name}
          locationLine={locationLabel}
          contactLine={`📞 ${shop.phone || ""}${
            shop.email ? ` • ✉️ ${shop.email}` : ""
          }`}
          logoUrl={shop.business?.logo?.url || shop.logoUrl}
          backgroundImage={shop.images?.[0]?.url || shop.images?.[0]?.path}
          schedule={shop.schedule}
          isMobile={isMobile}
          actions={[
            {
              label: "Back to Shop",
              onClick: () =>
                navigate(`/business/${businessSlug}/${locationSlug}`),
              variant: "solid",
            },
            ...(hasProducts
              ? [
                  {
                    label: "Shop Catalogue",
                    onClick: () => navigate(`/shop/${locationSlug}/products`),
                    variant: "ghost",
                  },
                ]
              : []),
          ]}
        />

        <main className="pkg-shell pkg-body">
          {packages.length === 0 ? (
            <div className="pkg-empty">
              <h2 className="pkg-empty-title">No packages available</h2>
              <p className="pkg-empty-text">
                This shop has not published any packages yet.
              </p>
            </div>
          ) : (
            <>
              <p className="pkg-count">
                {packages.length} package{packages.length === 1 ? "" : "s"}
              </p>
              <ul className="pkg-grid">
                {packages.map((pkg) => (
                  <li key={pkg._id}>
                    <PackageOfferCard
                      pkg={pkg}
                      onSelect={(selected) => setBuyPromptPackage(selected)}
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
        </main>
      </div>

      <PackageBuyPromptModal
        pkg={buyPromptPackage}
        onClose={() => setBuyPromptPackage(null)}
      />

      <CustomerFooter />
    </>
  );
};

export default ShopPackages;
